import styled from "@emotion/styled"
import parseHTML from "html-react-parser"
import { find, get } from "lodash"
import PropTypes from "prop-types"
import React from "react"
import tw from "twin.macro"
import Link from "../components/Link"
import IconGridBlock from "./BlockIconGrid"
import IconListBlock from "./BlockIconList"
import ImageBlock from "./BlockImage"
import LinksBlock from "./BlockLinks"
import Blockquote from "./Blockquote"
import VideoBlock from "./BlockVideo"
import { LinkContentItem } from "./Links"

const StyledRichText = styled.div`
  ${(props) => props.alignText === "center" && tw`text-center`}
  ${(props) =>
    props.contrastText &&
    tw`text-white`}
  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6 {
    ${tw`leading-tight`}
  }
  > h2 {
    ${tw`text-2xl font-bold font-display uppercase`}
    ${(props) => !props.contrastText && tw`text-blue`}
  }
  > h3 {
    ${tw`text-xl font-bold font-display uppercase text-gray`}
  }
  > h4 {
    ${tw`text-lg font-bold font-display uppercase text-gray`}
  }
  > h5,
  > h6 {
    ${tw`text-base font-bold font-display uppercase text-gray`}
  }
  > p,
  > ol,
  > ul {
    ${tw`leading-loose`}
  }
  > ol,
  > ul {
    ${tw`list-outside ml-6`}
    li {
      ${tw`leading-normal`}
    }
    li + li {
      ${tw`mt-2`}
    }
  }
  > ul {
    ${tw`list-disc`}
  }
  > ol {
    ${tw`list-decimal`}
  }
  > * + * {
    ${tw`mt-4`}
  }
  > * + h1,
  > * + h2,
  > * + h3,
  > * + h4,
  > * + h5,
  > * + h6,
  > * + .c-spacer--lg {
    ${tw`mt-8`}
  }

  > h3 + p,
  > h4 + p,
  > h5 + p,
  > h6 + p {
    ${tw`mt-2`}
  }
  a:not([class]) {
    ${tw`underline text-blue hover:text-gray hover:no-underline`}
  }
  > table {
    ${tw`table-auto leading-normal text-sm`}
    th {
      ${tw`px-4 py-2 font-bold`}
    }
    td {
      ${tw`border px-4 py-2 border-gray-light align-top`}
    }
  }
`

const RichText = ({
  children,
  linkedItems,
  links,
  styled = true,
  contrastText,
  region,
  ...other
}) => {
  if (!children || !children.length) {
    return null
  }

  // Remove any line breaks from HTML.
  const cleaned = children.replace(/(<p><br><\/p>|\n|\r)+/, "")

  // Parse HTML as React components, replacing any content items.
  const html = parseHTML(cleaned, {
    replace: (domNode) =>
      replaceLinkedItems(domNode, linkedItems, links, contrastText, region),
  })

  // Return all components inside a fragment.
  if (styled) {
    return (
      <StyledRichText contrastText={contrastText} {...other}>
        {html}
      </StyledRichText>
    )
  } else {
    return <div {...other}>{html}</div>
  }
}

RichText.propTypes = {
  children: PropTypes.node.isRequired,
  linkedItems: PropTypes.array,
  currentLanguage: PropTypes.string,
}

// Get code name for Kentico Cloud inline content item from DOM node.
const getCodeName = (domNode) => {
  return get(domNode, 'attribs["data-codename"]') || null
}

// Get content for Kentico Cloud inline link from DOM node.
const getLinkContent = (domNode) => {
  return get(domNode, "children[0].data") || null
}

// Get data for Kentico Cloud inline content item.
const getLinkedItem = (codename, linkedItems) => {
  return find(linkedItems, (item) => {
    if (!get(item, "system.codename")) {
      console.log(item.__typename)
    }
    return get(item, "system.codename") === codename
  })
}

// Get href for Kentico Cloud inline link from DOM node
const getLinkHref = (domNode) => {
  return domNode.attribs && domNode.attribs["href"]
}

// Get href for content item from item in links array
const getLinkFromContentItem = (domNode, links) => {
  const linkId = domNode.attribs && domNode.attribs["data-item-id"]
  const link = links && links.find((link) => link.link_id === linkId)

  if (link) {
    return get(link, "modular_content.resolved_url") || getLinkHref(domNode)
  }

  return getLinkHref(domNode)
}

// Check if DOM node is a Kentico Cloud inline link.
const isLink = (domNode) => {
  return (
    domNode.name === "a" && domNode.attribs && domNode.attribs["data-item-id"]
  )
}

const isLinkedItem = (domNode) => {
  return (
    domNode.name === "object" &&
    !!domNode.attribs &&
    domNode.attribs.type === "application/kenticocloud"
  )
}

// Replace HTML DOM node with React component.
const replaceLinkedItems = (domNode, linkedItems, links, contrastText, region) => {
  // Replace inline links.
  if (isLink(domNode)) {
    let linkTo = isLink(domNode)
      ? getLinkFromContentItem(domNode, links)
      : getLinkHref(domNode)
    const content = getLinkContent(domNode)

    return <Link to={linkTo}>{content}</Link>
  }

  // Replace inline linked items.
  if (isLinkedItem(domNode)) {
    const codename = getCodeName(domNode)

    if (!codename) {
      return false
    }

    const linkedItem = getLinkedItem(codename, linkedItems)

    if (!linkedItem) {
      return false
    }

    return (
      <LinkedItem
        linkedItem={linkedItem}
        contrastText={contrastText}
        region={region}
      />
    )
  }
}

const LinkedItem = ({ linkedItem, contrastText, region }) => {
  return (
    {
      blockquote: <Blockquote block={linkedItem} />,
      icon_list: (
        <div className={region !== `Icon List` ? `c-spacer--lg` : null}>
          <IconListBlock block={linkedItem} region="Icon List" />
        </div>
      ),
      icon_grid: (
        <div className="c-spacer--lg">
          <IconGridBlock block={linkedItem} />
        </div>
      ),
      image_block: <ImageBlock block={linkedItem} />,
      link_content_item: (
        <p>
          <LinkContentItem contentItem={linkedItem} />
        </p>
      ),
      links: <LinksBlock block={linkedItem} />,
      video_block: <VideoBlock block={linkedItem} />,
    }[linkedItem.system.type] || (
      <p style={{ background: `red` }}>{linkedItem.system.type}</p>
    )
  )
}

export default RichText
